import { useState } from 'react'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { FiltersDrawer } from '../../../../../../components/filtersDrawer'
import SimpleModal from '../../../../../../components/modal/simpleModal'
import { DinamicForm } from '../../../../../../components/dinamicForm'
import { useContextState } from '../../../../../../hooks/theme'
import { BreadCrumb, CustomTable, SnackSimple } from '../../../../../../components'
import { icons } from '../../../../../../utils/icons'
import { useGetData } from './hooks/useGetData'
import { useInput } from './hooks/useInputs'
import { useStyles } from './style'
import Loader from '../../../../../../components/loader'
import { Types } from '../../../../../../utils/enums'
import { departmentService } from '../../../../../../utils/constants/callService'
import { handleAllErrors } from '../../../../../../utils/methods/handleErrors'
import { useNavigate } from 'react-router-dom'
import { useDepartmentContext } from '../../context'
import { useDownload } from '../../../../../../hooks/download/useDownload'
import { returnQueryWithPagination } from '../../../../../../utils/methods/returnQuery'
import { ModelDownloadButtons } from '../../../../../../components/donwloadFiles/model'

const Department = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { filterMain } = useDepartmentContext()
  const { counter, errors, filterData, paginationModel, handlePagination, onChange, cleanFilter, sortTable, query, filterString } = filterMain
  const { items, table, loadingTable, requestTable, totalPages } = useGetData({ filterMain })
  const { input } = useInput({ items })
  const [openModal, setOpenModal] = useState({
    id: 0,
    open: false,
  })
  const column: any[] = [
    {
      id: 'createTime',
      label: t('general.tags.createTime'),
      sortable: true,
      minWidth: 100,
    },
    {
      id: 'areaName',
      label: t('general.tags.area'),
      sortable: true,
      minWidth: 100,
    },
    {
      id: 'name',
      label: t('general.tags.department'),
      sortable: true,
      minWidth: 100,
    },
    {
      id: 'email',
      label: t('general.tags.email'),
      sortable: true,
      minWidth: 100,
    },
    {
      id: 'phone',
      label: t('general.tags.number'),
      sortable: true,
      minWidth: 100,
    },
    {
      id: 'action',
      label: t('general.tags.action'),
      minWidth: 50,
      renderCell: (params: any) => {
        return (
          <Box sx={styles.action}>
            <Tooltip title={t('ToolTips.delete')}>
              <IconButton
                sx={styles.deleteIcon}
                onClick={(e) => {
                  e.stopPropagation()
                  setOpenModal((prev) => ({
                    ...prev,
                    id: params?.row?.id,
                    open: true,
                  }))
                }}
              >
                {icons('delete')}
              </IconButton>
            </Tooltip>
          </Box>
        )
      },
    },
  ]

  const { colors } = useContextState()
  const styles = useStyles(colors)
  const [btnLoading, setBtnLoading] = useState(false)
  const [openSnackbar, setOpenSnackbar] = useState({
    open: false,
    message: '',
    type: Types.SUCCESS,
  })

  const handleCloseSnackbar = (value: boolean) => setOpenSnackbar({ ...openSnackbar, open: value })

  const handleCloseModal = () => setOpenModal((prev) => ({ ...prev, open: false }))

  const handleDelete = async () => {
    if (openModal.id) {
      setBtnLoading(true)
      const response = await departmentService.deleteDepartment(openModal.id)

      if (!response.errors) {
        setOpenSnackbar({
          open: true,
          type: Types.SUCCESS,
          message: t(`department.snackbar.delete.success`),
        })
        requestTable()
      } else {
        let obj = {
          open: true,
          type: Types.ERROR,
          message: handleAllErrors(response.errors[0].code, t, t(`department.snackbar.delete.error`)),
        }

        setOpenSnackbar(obj)
      }
      setBtnLoading(false)
      handleCloseModal()
    }
  }

  const handleCreate = () => {
    navigate('create')
  }

  const handleEdit = (e: any) => {
    navigate(`edit/${e.id}`)
  }

  const downloadMain = useDownload({
    docName: t('general.tags.departments'),
    query: returnQueryWithPagination({ filter: filterString, sort: query?.sort, }).replace('&', ''),
    downloadFuntion: departmentService.downloadFile,
  })

  return (
    <Box sx={styles.containerTable}>
      {/* HEADER */}
      <BreadCrumb
        removeBreadcrumb={['organization-chart']}
        replaceLabels={{ departments: 'departments-chart' }}
      />
      <Box sx={[styles.header, styles.actions]}>
        <Typography variant='title_grey_big'>{t('general.tags.departments')}</Typography>

        <Box sx={styles.flex}>
          <ModelDownloadButtons
            csv={downloadMain.downloadCSV}
            pdf={downloadMain.downloadPDF}
            word={downloadMain.downloadWORD}
            disableCSV={downloadMain.download.excel}
            disablePDF={downloadMain.download.pdf}
            disableWORD={downloadMain.download.word}
            styleType
          />

          <Tooltip title={t('ToolTips.clearFilters')}>
            <IconButton
              sx={(styles.clean, (counter || 0) > 0 ? null : styles.hide)}
              onClick={() => {
                cleanFilter()
              }}
            >
              {icons('clean')}
            </IconButton>
          </Tooltip>

          <Tooltip title={t('ToolTips.create')}>
            <IconButton className="add" onClick={handleCreate}>
              {icons('add')}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* FLOATS */}
      <FiltersDrawer cleanFilter={cleanFilter} count={counter} right={0} top={50}>
        <Box sx={styles.section}>
          <DinamicForm input={Object.values(input)} values={filterData} onChange={onChange} filter={true} errors={errors} />
        </Box>
      </FiltersDrawer>

      <SnackSimple close={handleCloseSnackbar} open={openSnackbar.open} type={openSnackbar.type} message={openSnackbar.message} />

      <SnackSimple
        close={downloadMain.closeOpenSnakbar}
        open={downloadMain.openSnackbar.open}
        type={downloadMain.openSnackbar.type as Types}
        message={downloadMain.openSnackbar.message}
      />

      <SimpleModal
        loadingButton
        isLoading={btnLoading}
        open={openModal.open}
        close={handleCloseModal}
        title={t('department.modal.delete.title')}
        description={''}
        buttonText={t('general.confirm')}
        cancelText={t('general.cancel')}
        color={colors}
        back={handleCloseModal}
        next={handleDelete}
      />

      {/* TABLE */}
      {!loadingTable ?
        <CustomTable
          onClick={handleEdit}
          name={t('general.tags.departments')}
          column={column}
          row={table}
          color={colors}
          selected={-1}
          order={
            query.sort && {
              count: query?.sort?.count,
              key: query?.sort?.field,
              order: query?.sort?.sort,
            }
          }
          sortable={(field, sort, count) => {
            sortTable(field && sort ? { field: field, sort: sort, count } : undefined)
          }}
          serverSidePagination={{
            page: (paginationModel.page ?? 0) + 1,
            size: paginationModel?.pageSize ?? 20,
            totalPages: totalPages,
          }}
          handleChange={(page: number, pageSize: number) => {
            handlePagination({ page, pageSize })
          }}
        />
        :
        <Loader />
      }
    </Box>
  )
}

export default Department
